import type {PublicationContext, Subscription} from 'centrifuge';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PosterCollaboration} from '@PosterWhiteboard/collaboration/poster-collaboration.class';
import type {PosterCollaborationConnection} from '@PosterWhiteboard/collaboration/poster-collaboration-connection.class';
import type {FetchOpsResponse, FlushOpsResponse, LiveEditsState, LocalOpEnvelope, PublishedOpEnvelope, PublishOpsResponse} from '@PosterWhiteboard/collaboration/poster-live-edits.types';
import {
  CATCH_UP_RETRY_DELAY_MS,
  DEFERRED_APPLY_POLL_MS,
  FLUSH_TIMEOUT_MS,
  LIVE_ENVELOPE_WINDOW_MS,
  MAX_OPS_PER_ENVELOPE,
  PUBLISH_RETRY_BASE_DELAY_MS,
  PUBLISH_RETRY_MAX_DELAY_MS,
  PublishTransport,
  SOLO_ENVELOPE_WINDOW_MS,
} from '@PosterWhiteboard/collaboration/poster-live-edits.types';
import type {PosterOp, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import {confirmEnvelope, isPublishedOpEnvelope, replayLocalEdits, withPendingEnvelope} from '@PosterWhiteboard/collaboration/live-edits-state.library';
import {derivePosterOps} from '@PosterWhiteboard/collaboration/derive-poster-ops.library';
import {canPublishWithKeepalive, publishWithKeepalive} from '@PosterWhiteboard/collaboration/keepalive-publish.library';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {getUserId} from '@Libraries/user.library';

const PUBLISH_OPS_ENDPOINT = 'stream/publishPosterOps';
const FETCH_OPS_ENDPOINT = 'stream/getPosterOps';
const FLUSH_OPS_ENDPOINT = 'stream/flushPosterOps';
/** Error codes under which the server refuses the envelope itself; retrying cannot change the answer. */
const REJECTION_ERROR_CODES: ReadonlySet<string> = new Set(['ops-rejected', 'cannot-edit-poster', 'collaboration-disabled']);

interface AjaxErrorResponse {
  code?: string | null;
}

type AcknowledgementWaiter = (streamEnded: Error | null) => void;

/**
 * The tab's half of saving by op stream. Every local commit becomes ops; ops made within a short
 * window travel as one envelope, sent one at a time in commit order. The server orders each
 * envelope, stores it durably and answers with its sequence, which is the moment the edit is
 * saved. Every ordered envelope (this tab's own included) advances the confirmed poster, and what
 * the user sees is the confirmed poster with this tab's still-unordered commits replayed on top.
 * A gap in sequence numbers is filled from the op log. While collaborating, the server's channel
 * delivers the other tabs' envelopes; remote edits are held while the user is mid-gesture on the
 * canvas and applied when the gesture ends.
 */
export class PosterLiveEdits {
  #state: LiveEditsState | null = null;
  #connection: PosterCollaborationConnection | null = null;
  #subscription: Subscription | null = null;
  #inFlight: LocalOpEnvelope | null = null;
  /** Envelopes the server has accepted but whose ordered publication this tab has not seen yet; they stay pending but are not re-sent. */
  readonly #publishedOpIds = new Set<string>();
  #bufferedOps: PosterOp[] = [];
  #bufferTimer: ReturnType<typeof setTimeout> | null = null;
  #retryTimer: ReturnType<typeof setTimeout> | null = null;
  #retryDelayMs = PUBLISH_RETRY_BASE_DELAY_MS;
  #catchUpRetryTimer: ReturnType<typeof setTimeout> | null = null;
  #deferTimer: ReturnType<typeof setTimeout> | null = null;
  #sync: Promise<void> = Promise.resolve();
  #isSyncQueued = false;
  #isCatchingUp = false;
  #isCatchUpRequested = false;
  #envelopeCount = 0;
  /** Bumped on every open and leave, so a publish that completes after its session ended is ignored. */
  #sessionGeneration = 0;
  #acknowledgementWaiters: AcknowledgementWaiter[] = [];

  constructor(
    private readonly poster: Poster,
    private readonly collaboration: PosterCollaboration
  ) {}

  /** Whether this tab saves by op stream at all. */
  public isActive(): boolean {
    return this.#state !== null;
  }

  /** Whether the other tabs' envelopes reach this tab live, as opposed to only through its own catch-ups. */
  public isCollaborating(): boolean {
    return this.#subscription !== null;
  }

  /** True while any local commit is still unsent or unordered: the moment it turns false, everything is saved. */
  public hasUnacknowledgedEdits(): boolean {
    return this.#bufferedOps.length > 0 || (this.#state !== null && this.#state.pending.length > 0);
  }

  /**
   * Starts tracking from `confirmedSnapshot`, the poster as the stored JSON plus `loadedSeq` describe
   * it. Synchronous so that commits made while the connection is still opening are queued, not lost.
   */
  public open(loadedSeq: number, confirmedSnapshot: SnapshotRecord): void {
    this.#sessionGeneration += 1;
    this.#state = {confirmed: confirmedSnapshot, lastAppliedSeq: loadedSeq, pending: []};
  }

  /** Subscribes to the poster's edits and replays what the log holds beyond the loaded JSON. */
  public async join(connection: PosterCollaborationConnection): Promise<void> {
    this.#connection = connection;
    this.#subscription = connection.subscribe('edits', this.#onPublication.bind(this));
    await this.#catchUp();
  }

  /**
   * Replays what the log holds beyond the loaded JSON for a tab that does not join the live
   * channels: the editor's load reads the stored snapshot as is, so edits a fold has not reached
   * yet would otherwise stay hidden until this tab's own next publish.
   */
  public async replayLog(): Promise<void> {
    await this.#catchUp();
  }

  /** Ends the stream; a flush still waiting for acknowledgements is told that nothing more will be acknowledged. */
  public leave(): void {
    this.#sessionGeneration += 1;
    if (this.#subscription !== null) {
      this.#connection?.unsubscribe(this.#subscription);
    }
    this.#subscription = null;
    this.#connection = null;
    this.#state = null;
    this.#inFlight = null;
    this.#bufferedOps = [];
    this.#publishedOpIds.clear();
    this.#clearTimers();
    this.#settleAcknowledgementWaiters(new Error('PosterLiveEdits: the stream ended before every edit was acknowledged'));
  }

  /** Called by PosterHistory for every commit; the difference joins this tab's next envelope. */
  public onLocalCommit(before: SnapshotRecord, after: SnapshotRecord): void {
    if (this.#state === null) {
      return;
    }
    const ops = derivePosterOps(before, after);
    if (ops.length === 0) {
      return;
    }
    this.#bufferedOps.push(...ops);
    if (this.#bufferedOps.length >= MAX_OPS_PER_ENVELOPE) {
      this.#sendBufferedOps(PublishTransport.AJAX);
    } else {
      this.#scheduleBufferedSend();
    }
    this.#notifySaveStatus();
  }

  /**
   * Sends everything this tab still holds, waits until the server has ordered it, then has the
   * server fold the log into the stored poster. Resolves with the sequence the stored poster reflects.
   */
  public async flush(): Promise<number> {
    if (this.#state === null) {
      throw new Error('PosterLiveEdits: flush before open');
    }
    this.#sendBufferedOps(PublishTransport.AJAX);
    await this.#waitForAcknowledgements();
    return this.foldLog();
  }

  /**
   * Has the server fold whatever the log holds into the stored poster, open stream or not, and
   * resolves with the sequence the stored poster then reflects.
   */
  public async foldLog(): Promise<number> {
    const response = (await window.PMW.writeLocal(FLUSH_OPS_ENDPOINT, {hashedId: this.poster.hashedID})) as FlushOpsResponse;
    return response.opSeq;
  }

  /**
   * The page is closing: what is still buffered goes out as a request the browser finishes even
   * after the page is gone. If the user then stays, the answer is handled like any other.
   */
  public sendBufferedOpsBeforeUnload(): void {
    this.#sendBufferedOps(PublishTransport.KEEPALIVE);
  }

  #scheduleBufferedSend(): void {
    if (this.#bufferTimer !== null) {
      return;
    }
    const windowMs = this.collaboration.isSharedWithOthers() ? LIVE_ENVELOPE_WINDOW_MS : SOLO_ENVELOPE_WINDOW_MS;
    this.#bufferTimer = setTimeout((): void => {
      this.#bufferTimer = null;
      this.#sendBufferedOps(PublishTransport.AJAX);
    }, windowMs);
  }

  /**
   * Nothing leaves while an image uploads, as whole-poster saving pauses then too: the add op of an
   * uploading image carries no file yet, and a fold that met it would fail on every later attempt.
   * The upload's resolution joins the same buffer, so the image goes out complete.
   */
  #sendBufferedOps(transport: PublishTransport): void {
    if (this.#bufferTimer !== null) {
      clearTimeout(this.#bufferTimer);
      this.#bufferTimer = null;
    }
    if (this.#state === null || this.#bufferedOps.length === 0) {
      return;
    }
    if (this.poster.areAnyImagesUploading()) {
      this.#scheduleBufferedSend();
      return;
    }
    this.#envelopeCount += 1;
    const envelope: LocalOpEnvelope = {
      opId: `${this.collaboration.sessionId}:${this.#envelopeCount}`,
      sessionId: this.collaboration.sessionId,
      posterVersion: POSTER_VERSION.CURRENT,
      ops: this.#bufferedOps,
    };
    this.#bufferedOps = [];
    this.#state = withPendingEnvelope(this.#state, envelope);
    this.#sendNext(transport);
  }

  #sendNext(transport: PublishTransport): void {
    if (this.#state === null || this.#inFlight !== null || this.#retryTimer !== null) {
      return;
    }
    const envelope = this.#state.pending.find((pendingEnvelope) => !this.#publishedOpIds.has(pendingEnvelope.opId));
    if (envelope === undefined) {
      return;
    }
    this.#inFlight = envelope;
    const generation = this.#sessionGeneration;
    void this.#publish(envelope, transport).then(
      (response: unknown): void => {
        if (!isPublishOpsResponse(response)) {
          this.#onPublishFailed(generation, new Error('PosterLiveEdits: the publish answer carried no sequence'));
          return;
        }
        this.#onPublished(generation, envelope, response);
      },
      this.#onPublishFailed.bind(this, generation)
    );
  }

  async #publish(envelope: LocalOpEnvelope, transport: PublishTransport): Promise<unknown> {
    const fields = {hashedId: this.poster.hashedID, envelope: JSON.stringify(envelope)};
    if (transport === PublishTransport.KEEPALIVE && canPublishWithKeepalive(fields)) {
      return publishWithKeepalive(PUBLISH_OPS_ENDPOINT, fields);
    }
    return window.PMW.writeLocal(PUBLISH_OPS_ENDPOINT, fields);
  }

  /**
   * The server has ordered and stored the envelope: it is saved. Its ordered form is confirmed
   * from the answer right away; a sequence that is not the next one means other envelopes came
   * first, and the log is read to apply them in order.
   */
  #onPublished(generation: number, envelope: LocalOpEnvelope, response: PublishOpsResponse): void {
    if (generation !== this.#sessionGeneration || this.#state === null) {
      return;
    }
    this.#inFlight = null;
    this.#retryDelayMs = PUBLISH_RETRY_BASE_DELAY_MS;
    this.#publishedOpIds.add(envelope.opId);
    const published: PublishedOpEnvelope = {...envelope, seq: response.seq, userId: currentUserId()};
    if (response.isDuplicate || published.seq !== this.#state.lastAppliedSeq + 1) {
      void this.#catchUp();
    } else {
      this.#confirm(published);
    }
    this.#sendNext(PublishTransport.AJAX);
    this.#notifySaveStatus();
  }

  /** The same envelope is sent again after a pause that doubles with every failure in a row, so a dead server is not hammered. */
  #onPublishFailed(generation: number, error: unknown): void {
    if (generation !== this.#sessionGeneration) {
      return;
    }
    this.#inFlight = null;
    if (isRejection(error)) {
      this.#abandonOpStream(error);
      return;
    }
    this.poster.savePoster.onOpStreamPublishFailed();
    this.#retryTimer = setTimeout((): void => {
      this.#retryTimer = null;
      this.#sendNext(PublishTransport.AJAX);
    }, this.#retryDelayMs);
    this.#retryDelayMs = Math.min(this.#retryDelayMs * 2, PUBLISH_RETRY_MAX_DELAY_MS);
  }

  /**
   * A refused envelope cannot be made acceptable by retrying, and the poster on screen still holds
   * every edit, so the stream ends for this session and the poster goes back to saving whole: the
   * save class sees the stream inactive and resumes its own path.
   */
  #abandonOpStream(error: unknown): void {
    if (this.#state === null) {
      return;
    }
    window.PMW.log('poster-live-edits: envelope rejected, saving the poster whole from here on', error);
    this.collaboration.stop();
    this.poster.savePoster.onOpStreamAbandoned();
  }

  #onPublication(context: PublicationContext): void {
    const envelope: unknown = context.data;
    if (this.#state === null || !isPublishedOpEnvelope(envelope) || envelope.seq <= this.#state.lastAppliedSeq) {
      return;
    }
    if (envelope.seq > this.#state.lastAppliedSeq + 1) {
      void this.#catchUp();
      return;
    }
    this.#confirm(envelope);
  }

  #confirm(envelope: PublishedOpEnvelope): void {
    if (this.#state === null) {
      return;
    }
    const isOwn = envelope.sessionId === this.collaboration.sessionId;
    this.#state = confirmEnvelope(this.#state, envelope);
    this.#publishedOpIds.delete(envelope.opId);
    if (!isOwn) {
      this.#scheduleSync();
    }
    this.#notifySaveStatus();
  }

  /**
   * Reads every envelope this tab has not applied from the log, in order, then shows the result.
   * A request made while a read is running is served by one more read afterwards; a read that
   * fails is tried again shortly, so an own envelope the log has ordered cannot stay unconfirmed.
   */
  async #catchUp(): Promise<void> {
    if (this.#isCatchingUp) {
      this.#isCatchUpRequested = true;
      return;
    }
    this.#isCatchingUp = true;
    try {
      await this.#confirmLogUntilQuiet();
      this.#scheduleSync();
    } catch (error) {
      window.PMW.log('poster-live-edits: could not read the op log', error);
      this.#scheduleCatchUpRetry();
    } finally {
      this.#isCatchingUp = false;
    }
  }

  /** One more read serves any request made while the previous read was running. */
  async #confirmLogUntilQuiet(): Promise<void> {
    this.#isCatchUpRequested = false;
    await this.#confirmLogPagesFrom(this.#state?.lastAppliedSeq ?? null);
    if (this.#isCatchUpRequested) {
      await this.#confirmLogUntilQuiet();
    }
  }

  #scheduleCatchUpRetry(): void {
    if (this.#catchUpRetryTimer !== null || this.#state === null) {
      return;
    }
    this.#catchUpRetryTimer = setTimeout((): void => {
      this.#catchUpRetryTimer = null;
      void this.#catchUp();
    }, CATCH_UP_RETRY_DELAY_MS);
  }

  /**
   * The log is read one page at a time, each page starting where the previous one ended, until a
   * page comes back empty. A page that moves nothing forward means the log no longer holds the
   * envelope this tab needs next (it expired while the tab was away), so this tab can never
   * rejoin the shared sequence: the stream ends and the poster saves whole from here on.
   */
  async #confirmLogPagesFrom(afterSeq: number | null): Promise<void> {
    if (afterSeq === null) {
      return;
    }
    const envelopes = await this.#fetchAfter(afterSeq);
    if (envelopes.length === 0) {
      return;
    }
    for (const envelope of envelopes) {
      this.#confirm(envelope);
    }
    const reachedSeq = this.#state?.lastAppliedSeq ?? null;
    if (reachedSeq === afterSeq) {
      this.#abandonOpStream(new Error(`PosterLiveEdits: the op log no longer holds the envelope after seq ${afterSeq}`));
      return;
    }
    await this.#confirmLogPagesFrom(reachedSeq);
  }

  async #fetchAfter(seq: number): Promise<PublishedOpEnvelope[]> {
    const response = (await window.PMW.readLocal(FETCH_OPS_ENDPOINT, {hashedId: this.poster.hashedID, afterSeq: seq})) as FetchOpsResponse;
    return response.envelopes.filter(isPublishedOpEnvelope);
  }

  /** Serialises applies: one runs at a time, and a request made meanwhile runs once after it with the latest state. */
  #scheduleSync(): void {
    if (this.#isSyncQueued) {
      return;
    }
    this.#isSyncQueued = true;
    this.#sync = this.#sync.then(this.#syncLivePoster.bind(this)).catch(this.#onSyncFailed.bind(this));
  }

  /** One failed apply must not stop every later one: the chain stays resolved and the flag is released. */
  #onSyncFailed(error: unknown): void {
    this.#isSyncQueued = false;
    window.PMW.log('poster-live-edits: could not apply the shared poster', error);
  }

  async #syncLivePoster(): Promise<void> {
    this.#isSyncQueued = false;
    if (this.#state === null) {
      return;
    }
    if (this.poster.isLocalGestureInProgress()) {
      this.#applyWhenGestureEnds();
      return;
    }
    await this.poster.history.applyCollaborativeSnapshot(replayLocalEdits(this.#state, this.#bufferedOps));
  }

  #applyWhenGestureEnds(): void {
    if (this.#deferTimer !== null) {
      return;
    }
    this.#deferTimer = setTimeout((): void => {
      this.#deferTimer = null;
      this.#scheduleSync();
    }, DEFERRED_APPLY_POLL_MS);
  }

  #waitForAcknowledgements(): Promise<void> {
    if (!this.hasUnacknowledgedEdits()) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject): void => {
      const timeout = setTimeout((): void => {
        this.#acknowledgementWaiters = this.#acknowledgementWaiters.filter((waiter) => waiter !== onSettled);
        reject(new Error('PosterLiveEdits: the server did not acknowledge every edit in time'));
      }, FLUSH_TIMEOUT_MS);
      const onSettled: AcknowledgementWaiter = (streamEnded): void => {
        clearTimeout(timeout);
        if (streamEnded === null) {
          resolve();
        } else {
          reject(streamEnded);
        }
      };
      this.#acknowledgementWaiters.push(onSettled);
    });
  }

  #notifySaveStatus(): void {
    const hasUnacknowledgedEdits = this.hasUnacknowledgedEdits();
    if (!hasUnacknowledgedEdits) {
      this.#settleAcknowledgementWaiters(null);
    }
    this.poster.savePoster.onOpStreamSaveStatusChange(hasUnacknowledgedEdits);
  }

  #settleAcknowledgementWaiters(streamEnded: Error | null): void {
    const waiters = this.#acknowledgementWaiters;
    this.#acknowledgementWaiters = [];
    for (const waiter of waiters) {
      waiter(streamEnded);
    }
  }

  #clearTimers(): void {
    for (const timer of [this.#bufferTimer, this.#retryTimer, this.#catchUpRetryTimer, this.#deferTimer]) {
      if (timer !== null) {
        clearTimeout(timer);
      }
    }
    this.#bufferTimer = null;
    this.#retryTimer = null;
    this.#catchUpRetryTimer = null;
    this.#deferTimer = null;
    this.#retryDelayMs = PUBLISH_RETRY_BASE_DELAY_MS;
  }
}

const isPublishOpsResponse = (value: unknown): value is PublishOpsResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<PublishOpsResponse>;
  return typeof candidate.seq === 'number' && typeof candidate.isDuplicate === 'boolean';
};

/** The server said no to the envelope itself (as opposed to the request not getting through). */
const isRejection = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const {code} = error as AjaxErrorResponse;
  return typeof code === 'string' && REJECTION_ERROR_CODES.has(code);
};

const currentUserId = (): number => {
  const userId = Number(getUserId());
  return Number.isFinite(userId) ? userId : 0;
};
